<script lang="ts">
	import { A, Badge, Dropdown, DropdownItem, Range } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import { twMerge } from "tailwind-merge";
	import type { BrandKit } from "#gen/brands/v1/service_pb.js";
	import P from "#lib/components/P.svelte";
	import AlphaColorPicker from "#lib/components/flow/editor/appearance/AlphaColorPicker.svelte";
	import { normalizeHex } from "#lib/components/flow/editor/appearance/colorMath.js";
	import type { ColorMode } from "#lib/components/flow/viewer/layout/types.js";
	import { cmykInputToFcColorFormat, convertCMYKToHex, formatFcColor } from "#lib/generator/colors.js";
	import { getPrimaryColor, getSecondaryColor, getTertiaryColor } from "#lib/theme.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		value: string;
		resetColor?: string;
		display?: "block" | "inline";
		colorMode?: ColorMode;
		disabled?: boolean;
		class?: string;
		showBrandKit?: boolean;
		overridden?: boolean;
		resetLabel?: string;
		hideResetAtColor?: boolean;
		allowTransparent?: boolean;
		onReset?: () => void;
		oninput: (value: string) => void;
		onchange: (value: string) => void;
	}

	let { id, value, resetColor = "#ffffff", display = "block", colorMode = "RGB", disabled = false, class: className, showBrandKit = true, overridden = false, resetLabel = "Reset to page style", hideResetAtColor = false, allowTransparent = false, onReset, oninput, onchange }: Props = $props();

	const brandKit: Writable<BrandKit | undefined> = getContext("brandKit");

	let dropdownOpen = $state(false);
	let hasReset = $state(false);
	let cyan = $state(0);
	let magenta = $state(0);
	let yellow = $state(0);
	let black = $state(0);
	let brandKitValues = $derived(
		[
			{ name: "Primary", value: getPrimaryColor($brandKit)?.hex },
			{ name: "Secondary", value: getSecondaryColor($brandKit)?.hex },
			{ name: "Tertiary", value: getTertiaryColor($brandKit)?.hex },
		].filter((item): item is { name: string; value: string } => item.value != null),
	);
	let usingBrandColor = $derived(brandKitValues.find(colorObj => colorObj.value?.toLowerCase() === displayValue?.toLowerCase()));
	let colorAtFocus = "";

	// A stored "transparent" or short "#fff" should show as a full hex instead of raw text.
	function forDisplay(raw: string): string {
		return normalizeHex(raw, allowTransparent) ?? raw;
	}

	// The readout updates right away on an edit instead of waiting for the parent to echo the value back.
	// We only reseed from the prop when the change came from elsewhere, not from our own last emission.
	let lastEmitted: string | undefined;
	let displayValue = $state("");
	$effect.pre(() => {
		if (value !== lastEmitted) displayValue = forDisplay(value);
	});

	// While the field is focused this holds the user's draft, otherwise it mirrors the live color.
	let hexFocused = $state(false);
	let hexDraft = $state("");
	$effect.pre(() => {
		if (!hexFocused) hexDraft = displayValue;
	});

	// oninput is the live preview and onchange is the commit. commit fires both for reset and brand pick, which have no preview to build on.
	function emitInput(next: string) {
		lastEmitted = next;
		displayValue = next;
		oninput?.(next);
	}

	function emitChange(next: string) {
		lastEmitted = next;
		displayValue = next;
		onchange?.(next);
	}

	function commit(next: string) {
		emitInput(next);
		onchange?.(next);
	}

	function pickerInput(next: string) {
		emitInput(next);
	}

	function pickerChange(next: string) {
		emitChange(next);
	}

	function hexFocus(event: FocusEvent) {
		hexFocused = true;
		colorAtFocus = displayValue;
		(event.currentTarget as HTMLInputElement).select();
	}

	function hexInput() {
		// normalizeHex returns null for an incomplete draft, so partial input previews nothing.
		const next = normalizeHex(hexDraft, allowTransparent);
		if (next) emitInput(next);
	}

	function hexCommit() {
		const next = normalizeHex(hexDraft, allowTransparent);
		if (next) {
			// Only commit when the color actually changed, so tabbing through a field never fires a save.
			if (next !== colorAtFocus) emitChange(next);
			hexDraft = next;
		} else {
			hexDraft = displayValue;
		}
	}

	function hexBlur() {
		hexFocused = false;
		hexCommit();
	}

	function hexKeydown(event: KeyboardEvent) {
		const target = event.currentTarget as HTMLInputElement;
		if (event.key === "Enter") {
			event.preventDefault();
			target.blur();
		} else if (event.key === "Escape") {
			event.preventDefault();
			hexDraft = colorAtFocus;
			if (displayValue !== colorAtFocus) emitInput(colorAtFocus);
			target.blur();
		}
	}

	function cmykInput() {
		const hex = convertCMYKToHex(cyan, magenta, yellow, black);
		const cmyk = cmykInputToFcColorFormat(cyan, magenta, yellow, black);
		const fcColor = formatFcColor(hex, cmyk);

		oninput?.(fcColor);
	}

	function cmykChange() {
		const hex = convertCMYKToHex(cyan, magenta, yellow, black);
		const cmyk = cmykInputToFcColorFormat(cyan, magenta, yellow, black);
		const fcColor = formatFcColor(hex, cmyk);

		onchange?.(fcColor);
	}

	$effect(() => {
		cyan = Number.parseInt(value?.split(":")?.[1]?.match(/\d+/g)?.[0] || "0");
		magenta = Number.parseInt(value?.split(":")?.[1]?.match(/\d+/g)?.[1] || "0");
		yellow = Number.parseInt(value?.split(":")?.[1]?.match(/\d+/g)?.[2] || "0");
		black = Number.parseInt(value?.split(":")?.[1]?.match(/\d+/g)?.[3] || "0");
	});

	// The parent's `overridden` prop lags the live value for a moment after an edit commits, so we check
	// the displayed value here and the reset link never sticks once a color is back to its default.
	let atResetColor = $derived((displayValue ?? "").toLowerCase() === (resetColor ?? "").toLowerCase());

	function reset() {
		if (resetColor === displayValue) {
			hasReset = true;
		} else {
			commit(resetColor);
		}
	}

	function selectBrandColor(val?: string) {
		if (val) {
			commit(val);
			dropdownOpen = false;
			hasReset = false;
		}
	}
</script>

<div class={twMerge("flex items-center overflow-clip rounded-lg border border-gray-300 bg-gray-50", display === "block" ? "flex-col" : "justify-center", className)}>
	{#if colorMode === "RGB"}
		<div class="relative" style="background-color: {displayValue};" class:w-full={display === "block"} class:w-12={display !== "block"} class:h-16={display === "block"} class:h-10={display !== "block"}>
			<AlphaColorPicker {id} value={displayValue} {disabled} {allowTransparent} oninput={pickerInput} onchange={pickerChange} />
		</div>
		<div class="flex w-full justify-between p-2 text-left text-sm font-medium text-gray-600 uppercase" class:border-t={display === "block"} class:border-l={display === "inline"}>
			{#if usingBrandColor && !hasReset && showBrandKit}
				<Badge class="border-primary-200 bg-primary-100 text-primary-800 border border-dotted font-mono normal-case">{usingBrandColor.name} Color</Badge>
				<A onclick={reset} class="text-xs font-medium normal-case no-underline hover:no-underline">Reset</A>
			{:else}
				<input
					type="text"
					bind:value={hexDraft}
					{disabled}
					aria-label="Hex color"
					spellcheck="false"
					autocapitalize="off"
					autocomplete="off"
					autocorrect="off"
					maxlength={allowTransparent ? 9 : 7}
					class="hover:ring-primary-300 focus:ring-primary-500 inline-block flex-none rounded border-0 bg-transparent p-0 font-mono text-sm font-medium text-gray-600 uppercase hover:border-none hover:ring-1 hover:ring-offset-1 hover:outline-none focus:border-none focus:ring-2 focus:ring-offset-4 focus:outline-none active:border-none active:outline-none {allowTransparent
						? 'w-[10ch]'
						: 'w-[8ch]'}"
					onfocus={hexFocus}
					oninput={hexInput}
					onblur={hexBlur}
					onkeydown={hexKeydown}
				/>
				{#if overridden && onReset && !(hideResetAtColor && atResetColor)}
					<A onclick={onReset} class="text-xs font-medium whitespace-nowrap normal-case no-underline hover:no-underline">{resetLabel}</A>
				{/if}
				{#if brandKitValues.length > 0 && showBrandKit}
					<A class="text-xs font-medium normal-case">Pull from Brand Kit</A>
					<Dropdown simple class="max-w-sm rounded px-2 py-0 normal-case shadow" triggerDelay={0} transitionParams={{ duration: 0 }} placement="bottom-end" bind:isOpen={dropdownOpen}>
						{#each brandKitValues as colorObj (colorObj.name)}
							<DropdownItem class="flex items-center rounded-md" onclick={() => selectBrandColor(colorObj.value)}>
								<div class="mr-1 h-4 w-4 shrink-0" style="background-color: {colorObj.value};"></div>
								<P size="sm" class="shrink-0 whitespace-nowrap normal-case">{colorObj.name} Color - {colorObj.value?.toUpperCase()}</P>
							</DropdownItem>
						{/each}
					</Dropdown>
				{/if}
			{/if}
		</div>
	{:else}
		<div class="relative flex w-full">
			<div class="w-3/4">
				<div class="inset-0 flex flex-col gap-2 p-2">
					<div class="flex items-center gap-2">
						<span class="w-8 text-xs font-medium text-gray-600">C:</span>
						<Range size="sm" class="flex-1" min={0} max={100} bind:value={cyan} oninput={cmykInput} onchange={cmykChange} style="background: linear-gradient(to right, white, cyan);" />
						<span class="w-8 text-right text-xs font-medium text-gray-600">{cyan}%</span>
					</div>
					<div class="flex items-center gap-2">
						<span class="w-8 text-xs font-medium text-gray-600">M:</span>
						<Range size="sm" class="flex-1" min={0} max={100} bind:value={magenta} oninput={cmykInput} onchange={cmykChange} style="background: linear-gradient(to right, white, magenta);" />
						<span class="w-8 text-right text-xs font-medium text-gray-600">{magenta}%</span>
					</div>
					<div class="flex items-center gap-2">
						<span class="w-8 text-xs font-medium text-gray-600">Y:</span>
						<Range size="sm" class="flex-1" min={0} max={100} bind:value={yellow} oninput={cmykInput} onchange={cmykChange} style="background: linear-gradient(to right, white, yellow);" />
						<span class="w-8 text-right text-xs font-medium text-gray-600">{yellow}%</span>
					</div>
					<div class="flex items-center gap-2">
						<span class="w-8 text-xs font-medium text-gray-600">K:</span>
						<Range size="sm" class="flex-1" min={0} max={100} bind:value={black} oninput={cmykInput} onchange={cmykChange} style="background: linear-gradient(to right, white, black);" />
						<span class="w-8 text-right text-xs font-medium text-gray-600">{black}%</span>
					</div>
				</div>
			</div>
			<div class="w-1/4"><div class="h-full w-full" style="background-color: {value?.split(':')?.[0]}"></div></div>
		</div>
	{/if}
</div>
